import { Box, Divider, Grid, Skeleton, Stack } from "@mui/material";
import AppLayout from "@/components/AppLayout";
import Block from "@/components/Block";
import { useLayoutTier } from "@/hooks";
import BookingProgressIndicator from "./BookingProgressIndicator";
import TabletSummaryCardSkeleton from "./TabletSummaryCardSkeleton";

export default function BookingLayoutSkeleton() {
  const tier = useLayoutTier();

  const renderBookingSummarySkeleton = () => (
    <Block sx={{ p: "13px", height: "fit-content", border: 0 }}>
      <Stack spacing={2}>
        <Box sx={{ width: "100%" }}>
          <Skeleton variant="text" width="60%" height={30} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="50%" height={18} />
          <Skeleton variant="text" width="40%" height={18} />
          <Skeleton variant="text" width="40%" height={18} />
        </Box>
        <Divider />
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ width: "100%" }}>
            <Skeleton variant="text" width="40%" height={18} />
            <Skeleton variant="text" width="40%" height={18} />
            <Skeleton variant="text" width="20%" height={18} />
          </Box>
          <Skeleton variant="text" width={100} height={24} />
        </Box>
      </Stack>
    </Block>
  );

  const renderPriceOverviewSkeleton = () => (
    <Block>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Skeleton variant="text" width={150} height={32} />
        <Skeleton variant="circular" width={24} height={24} />
      </Stack>
      <Box>
        <Divider sx={{ my: 2, borderColor: "black" }} />
        <Box
          sx={{
            px: 0,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Skeleton variant="text" width={100} height={20} />
          <Skeleton variant="text" width={80} height={20} />
        </Box>
      </Box>
    </Block>
  );

  const renderCancellationPolicySkeleton = () => (
    <Block>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Skeleton variant="text" width={180} height={32} />
      </Stack>
      <Stack spacing={1}>
        <Skeleton variant="text" width="100%" height={20} />
        <Skeleton variant="text" width="90%" height={20} />
        <Skeleton variant="text" width="85%" height={20} />
      </Stack>
    </Block>
  );

  const renderPaymentInformationSkeleton = () => (
    <Block>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Skeleton variant="text" width={180} height={32} />
      </Stack>
      <Skeleton variant="text" width="100%" height={20} />
    </Block>
  );

  const renderExtrasSkeleton = () => (
    <Block flushOnMobile>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2, display: { xs: "none", sm: "flex" } }}
      >
        <Skeleton variant="text" width={200} height={32} />
      </Stack>
      <Stack spacing={2} divider={<Divider />}>
        <Skeleton variant="rectangular" width="100%" height={56} />
        <Skeleton variant="rectangular" width="100%" height={56} />
        <Skeleton variant="rectangular" width="100%" height={56} />
        <Skeleton variant="rectangular" width="100%" height={56} />
        <Skeleton variant="rectangular" width="100%" height={56} />
      </Stack>
    </Block>
  );

  const renderFormSectionSkeleton = (fieldCount = 3) => (
    <Block flushOnMobile>
      <Skeleton variant="text" width={180} height={32} sx={{ mb: 2 }} />
      <Stack spacing={2}>
        {Array.from({ length: fieldCount }, (_, i) => `field-${i}`).map((key) => (
          <Skeleton
            key={key}
            variant="rectangular"
            width="100%"
            height={56}
            sx={{ borderRadius: 1 }}
          />
        ))}
      </Stack>
    </Block>
  );

  return (
    <AppLayout title={<BookingProgressIndicator />}>
      <Grid container spacing={3} p={2}>
        {tier === "desktop" && (
          <Grid size={{ xs: 12, md: 4 }}>
            <Stack spacing={2}>
              {renderBookingSummarySkeleton()}
              {renderPriceOverviewSkeleton()}
              {renderCancellationPolicySkeleton()}
              {renderPaymentInformationSkeleton()}
            </Stack>
          </Grid>
        )}
        <Grid size={{ xs: 12, md: 8 }}>
          <Stack spacing={2}>
            {tier === "tablet" && <TabletSummaryCardSkeleton />}
            {renderExtrasSkeleton()}
            {renderFormSectionSkeleton(4)}
            {renderFormSectionSkeleton(2)}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ display: { xs: "none", sm: "none", md: "flex" } }}
            >
              <Skeleton variant="rectangular" width={120} height={48} sx={{ borderRadius: 20 }} />
              <Skeleton variant="rectangular" width={120} height={48} sx={{ borderRadius: 20 }} />
            </Stack>
          </Stack>
        </Grid>
      </Grid>

      {/* Mobile + tablet bottom bar */}
      <Box
        sx={{
          display: { xs: "block", sm: "block", md: "none" },
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: "background.paper",
          p: 2,
          boxShadow: "0 -4px 6px rgba(0, 0, 0, 0.1)",
          zIndex: "appBar",
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
          <Skeleton variant="text" width={80} height={32} />
          <Skeleton variant="text" width={90} height={20} />
        </Stack>
        <Skeleton variant="text" width="60%" height={20} sx={{ mb: 0.5 }} />
        <Skeleton variant="text" width="40%" height={20} sx={{ mb: 1.5 }} />
        <Skeleton variant="rectangular" width="100%" height={44} sx={{ borderRadius: 1 }} />
      </Box>
    </AppLayout>
  );
}
